import type { IClientCustomization } from "@gen/app-query";
import { zodResolver } from "@hookform/resolvers/zod";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  Fragment,
  type JSX,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Controller, useForm } from "react-hook-form";

import { PublicSvgIcon } from "~/components/public_svg_icon";
import { TextInput } from "~/components/text_input";
import { clientCustomizationFormSchema } from "~/entities/client_customization";

const HEX_COLOR_CODE_PREFIX = "#";

const MAX_LOGO_DIMENSIONS_PX = {
  width: 200,
  height: 60,
};

interface CustomizationEditorProps {
  customizationData?: IClientCustomization | null;
  isLoading?: boolean;
  canEdit?: boolean;
  onSave?: (values: IClientCustomization) => void;
}

export function CustomizationEditor({
  customizationData,
  isLoading = false,
  canEdit = false,
  onSave,
}: CustomizationEditorProps) {
  const memoValues = useMemo<IClientCustomization>(
    () => ({
      button_color_hex: customizationData?.button_color_hex ?? "",
      collect_phone_number: customizationData?.collect_phone_number ?? false,
      display_client_charge: customizationData?.display_client_charge ?? false,
      favicon_url: customizationData?.favicon_url ?? "",
      name: customizationData?.name ?? "",
      public_name: customizationData?.public_name ?? "",
      extra_price_label: customizationData?.extra_price_label ?? "",
      platform_commission_label:
        customizationData?.platform_commission_label ?? "",
      protection_fee_label: customizationData?.protection_fee_label ?? "",
      text_color_hex: customizationData?.text_color_hex ?? "",
      logo_url: customizationData?.logo_url ?? "",
    }),
    [customizationData],
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<IClientCustomization>({
    defaultValues: memoValues,
    resolver: canEdit ? zodResolver(clientCustomizationFormSchema) : undefined,
  });

  useEffect(() => {
    reset(memoValues);
  }, [memoValues, reset]);

  const [isLogoImageLoaded, setIsLogoImageLoaded] = useState(true);

  const onSubmit = (values: IClientCustomization) => {
    if (canEdit && onSave) {
      onSave(values);
    }
  };

  const renderTextField = (
    name: keyof IClientCustomization,
    label: ReactNode,
    placeholder: string,
    options?: {
      getter?: (val: string) => string;
      setter?: (val: string) => string;
      startInputAdornment?: ReactNode;
    },
  ) => (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        let value = field.value;
        if (options?.getter && typeof value === "string") {
          value = options.getter(value);
        }

        return (
          <TextInput
            {...field}
            label={label}
            placeholder={placeholder}
            value={value}
            clearable={canEdit}
            disabled={!canEdit}
            onClear={() => field.onChange("")}
            onChange={(e) =>
              field.onChange(
                options?.setter?.(e.target.value) ?? e.target.value,
              )
            }
            error={canEdit && !!fieldState.error}
            helperText={canEdit ? fieldState.error?.message : undefined}
            data-testid={`customization-${name}-input`}
            slotProps={{
              input: {
                ...(options?.startInputAdornment
                  ? {
                      startAdornment: (
                        <InputAdornment position="start">
                          {options?.startInputAdornment}
                        </InputAdornment>
                      ),
                    }
                  : {}),
              },
            }}
          />
        );
      }}
    />
  );

  const isDisabled = !isDirty;

  const hexColorOptions = {
    startInputAdornment: HEX_COLOR_CODE_PREFIX,
    setter: (val: string) => {
      if (val === "") {
        return val;
      }
      return HEX_COLOR_CODE_PREFIX + val;
    },
    getter: (val: string) => {
      if (val.length > 0 && val[0] === HEX_COLOR_CODE_PREFIX) {
        return val.slice(1);
      }
      return val;
    },
  };

  const renderLogoImage = (name: keyof IClientCustomization): JSX.Element => {
    const previewText =
      "Preview: How your logo will appear on the light grey checkout page.";
    const errorText =
      "Image failed to load. Please check the link and make sure it is public.";

    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          if (typeof field.value !== "string" || field.value === "") {
            return <Fragment />;
          }

          const errorFallbackImage = () => {
            return (
              <>
                <Stack
                  alignItems="center"
                  sx={{
                    maxWidth: MAX_LOGO_DIMENSIONS_PX.width,
                    maxHeight: MAX_LOGO_DIMENSIONS_PX.height,
                    backgroundColor: "error.50",
                  }}
                >
                  <PublicSvgIcon
                    name="image_failed"
                    sx={{
                      pt: 1,
                      pb: 1,
                    }}
                  />
                </Stack>
                <Typography sx={{ color: "error.500" }}>{errorText}</Typography>
              </>
            );
          };

          return (
            <Stack
              sx={{
                backgroundColor: "background.default",
                border: "1px solid",
                borderColor: "gray.200",
                borderRadius: 2,
                p: 2,
              }}
              flexDirection="column"
            >
              <Box
                component="img"
                src={field.value}
                alt="Logo"
                onError={() => {
                  setIsLogoImageLoaded(false);
                }}
                onLoad={() => {
                  setIsLogoImageLoaded(true);
                }}
                sx={{
                  maxWidth: MAX_LOGO_DIMENSIONS_PX.width,
                  maxHeight: MAX_LOGO_DIMENSIONS_PX.height,
                  objectFit: "contain",
                  backgroundColor: "gray.50",
                  display: isLogoImageLoaded ? "inherit" : "none",
                }}
              />
              {isLogoImageLoaded ? previewText : errorFallbackImage()}
            </Stack>
          );
        }}
      />
    );
  };

  return (
    <Stack spacing={5}>
      <Typography variant="h5SolidDivider">
        <PublicSvgIcon name="format_paint" /> Actions Page Customization
      </Typography>
      {renderTextField(
        "button_color_hex",
        "Button Color HEX",
        "Enter HEX Code",
        hexColorOptions,
      )}
      {renderTextField(
        "text_color_hex",
        "Text Color HEX",
        "Enter HEX Code",
        hexColorOptions,
      )}
      {renderTextField("favicon_url", "Favicon URL", "Enter URL")}
      <Stack flexDirection="column" spacing={1}>
        {renderTextField(
          "logo_url",
          <Stack flexDirection="row" alignItems="center">
            Logo URL&nbsp;
            <Tooltip
              title={`This logo will appear on the checkout page and emails. It will be automatically scaled to fit a maximum space of ${MAX_LOGO_DIMENSIONS_PX.width}x${MAX_LOGO_DIMENSIONS_PX.height} pixels.`}
            >
              <PublicSvgIcon name="info" />
            </Tooltip>
          </Stack>,
          "Enter URL",
        )}
        {renderLogoImage("logo_url")}
      </Stack>
      {renderTextField(
        "protection_fee_label",
        "Protection Fee Label",
        "Enter Protection Fee Label",
      )}
      {renderTextField(
        "name",
        <Stack flexDirection="row" alignItems="center">
          Client Name&nbsp;
          <Tooltip title="Shown on Actions page">
            <PublicSvgIcon name="info" />
          </Tooltip>
        </Stack>,
        "Enter Client Name",
      )}
      {renderTextField(
        "public_name",
        <Stack flexDirection="row" alignItems="center">
          Client Public Name&nbsp;
          <Tooltip title="Shown in emails">
            <PublicSvgIcon name="info" />
          </Tooltip>
        </Stack>,
        "Enter Client Public Name",
      )}
      {renderTextField(
        "extra_price_label",
        <Stack flexDirection="row" alignItems="center">
          Extra Price Label&nbsp;
          <Tooltip title="Shown on Actions page">
            <PublicSvgIcon name="info" />
          </Tooltip>
        </Stack>,
        "Enter Extra Price Label",
      )}
      {renderTextField(
        "platform_commission_label",
        <Stack flexDirection="row" alignItems="center">
          Platform Commission Label&nbsp;
          <Tooltip title="Shown on Actions page">
            <PublicSvgIcon name="info" />
          </Tooltip>
        </Stack>,
        "Enter Platform Commission Label",
      )}
      <Controller
        name="collect_phone_number"
        control={control}
        render={({ field }) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <Switch
              checked={field.value}
              disabled={!canEdit}
              onChange={(e) => field.onChange(e.target.checked)}
              slotProps={{
                input: {
                  "data-testid": "customization-collect_phone_number-switch",
                },
              }}
            />
            <Typography variant="body2">Collect phone number</Typography>
          </Stack>
        )}
      />
      <Controller
        name="display_client_charge"
        control={control}
        render={({ field }) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <Switch
              checked={field.value}
              disabled={!canEdit}
              onChange={(e) => field.onChange(e.target.checked)}
              slotProps={{
                input: {
                  "data-testid": "customization-display_client_charge-switch",
                },
              }}
            />
            <Typography variant="body2">
              Display platform commission separately
            </Typography>
          </Stack>
        )}
      />
      {canEdit && (
        <Button
          variant="contained"
          type="submit"
          loading={isLoading}
          disabled={isDisabled}
          onClick={handleSubmit(onSubmit)}
          size="large"
          data-testid="save-customization-button"
        >
          Save Customization
        </Button>
      )}
    </Stack>
  );
}
